import type { PhotoDto } from '@kominiarz/shared';
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { errorMessage } from '../../lib/api';
import { useDeletePhoto } from '../../lib/closingApi';
import { formatDateTime } from '@kominiarz/shared';
import { Button } from '../Button';
import { useConfirm } from '../Confirm';
import { Dialog } from '../Dialog';
import { useToast } from '../Toast';
import styles from './visit.module.css';

/** Thumbnails; a tap opens the photo full size with previous/next and delete. */
export function PhotoGallery({ photos }: { photos: PhotoDto[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const remove = useDeletePhoto();
  const confirm = useConfirm();
  const toast = useToast();
  const current = open !== null ? photos[open] : undefined;

  async function onDelete(photo: PhotoDto) {
    const ok = await confirm({
      title: 'Usunąć zdjęcie?',
      message: 'Zdjęcie zostanie usunięte na stałe.',
      confirmLabel: 'Usuń zdjęcie',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(photo.id);
      setOpen(null);
      toast.success('Zdjęcie usunięte');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  if (photos.length === 0) return null;
  return (
    <>
      <ul className={styles.gallery}>
        {photos.map((p, i) => (
          <li key={p.id}>
            <button type="button" className={styles.thumb} onClick={() => setOpen(i)}>
              <img src={`/api/photos/${p.id}/thumb`} alt={`Zdjęcie ${i + 1} z ${photos.length}`} loading="lazy" width="400" height="400" />
            </button>
          </li>
        ))}
      </ul>
      {current && open !== null && (
        <Dialog
          open
          title={`Zdjęcie ${open + 1} z ${photos.length}`}
          onClose={() => setOpen(null)}
          actions={
            <>
              {photos.length > 1 && (
                <>
                  <Button variant="secondary" icon={<ChevronLeft aria-hidden="true" />} onClick={() => setOpen((open - 1 + photos.length) % photos.length)}>
                    Poprzednie
                  </Button>
                  <Button variant="secondary" icon={<ChevronRight aria-hidden="true" />} onClick={() => setOpen((open + 1) % photos.length)}>
                    Następne
                  </Button>
                </>
              )}
              {current.canDelete && (
                <Button variant="danger" icon={<Trash2 aria-hidden="true" />} onClick={() => onDelete(current)}>
                  Usuń
                </Button>
              )}
            </>
          }
        >
          <img
            className={styles.full}
            src={`/api/photos/${current.id}`}
            alt={`Zdjęcie ${open + 1} z ${photos.length}`}
            width={current.width}
            height={current.height}
          />
          <p className={styles.photoMeta}>
            Dodano: {formatDateTime(current.createdAt)} ({current.uploadedBy.name})
          </p>
        </Dialog>
      )}
    </>
  );
}
